import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import dagre from 'dagre';
import { Button, Input } from 'antd';
import { edgeConditionSummary } from '@/modules/SystemAdministration/Jobs/JobChainTemplates/edgeConditions';
import {
  Background,
  Controls,
  Handle,
  MarkerType,
  MiniMap,
  Panel,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  type Connection,
  type Edge,
  type Node,
  type NodeProps,
  type OnBeforeDelete,
  type OnEdgesChange,
  type OnNodesChange,
  type XYPosition,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import type {
  JobChainTemplateGraph,
  JobChainTemplateMetadata,
} from '@/api/JobChainTemplates';
import { ConfirmDialog } from '@/modules/SystemAdministration/Jobs/JobChainTemplates/editorShared';
import { GraphProperties } from '@/modules/SystemAdministration/Jobs/JobChainTemplates/GraphProperties';
import { GraphWorkspace } from '@/modules/SystemAdministration/Jobs/JobChainTemplates/GraphAuthor.styled';
import { useTranslate } from '@/translations';

const NODE_WIDTH = 220;
const NODE_HEIGHT = 84;
const JOB_DRAG_TYPE = 'application/x-gba-template-job';
type CanvasNode = Node<
  {
    label: string;
    joinPolicy: string;
    deleted: boolean;
    start: boolean;
    end: boolean;
    connectionSource: boolean;
  },
  'templateJob'
>;

const TemplateFlowNode = ({
  data,
  selected,
  isConnectable,
}: NodeProps<CanvasNode>) => {
  const { t } = useTranslate();
  return (
    <div
      className="template-flow-node"
      data-selected={selected}
      data-deleted={data.deleted}
      data-source={data.connectionSource}
    >
      <Handle
        type="target"
        position={Position.Left}
        isConnectable={isConnectable}
        aria-label={t('jobChainTemplateGraphInputFor', { label: data.label })}
      />
      <div className="flow-node-title" title={data.label}>
        {data.label}
      </div>
      <div className="flow-node-meta">
        <span>
          {data.joinPolicy === 'AnyLive'
            ? t('jobChainTemplateGraphOrJoin')
            : data.joinPolicy === 'AllLive'
              ? t('jobChainTemplateGraphAndJoin')
              : data.joinPolicy}
        </span>
        {data.start && <span>{t('jobChainTemplateGraphStart')}</span>}
        {data.end && <span>{t('jobChainTemplateGraphEnd')}</span>}
        {data.deleted && <span>{t('jobChainTemplateGraphDeleted')}</span>}
      </div>
      <Handle
        type="source"
        position={Position.Right}
        isConnectable={isConnectable}
        aria-label={t('jobChainTemplateGraphOutputFrom', { label: data.label })}
      />
    </div>
  );
};
const nodeTypes = { templateJob: TemplateFlowNode };

const layoutPositions = (
  graph: JobChainTemplateGraph,
): Record<string, XYPosition> => {
  const layout = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  layout.setGraph({ rankdir: 'LR', nodesep: 48, ranksep: 96 });
  const keys = new Set(graph.nodes.map((node) => node.key));
  graph.nodes.forEach((node) =>
    layout.setNode(node.key, { width: NODE_WIDTH, height: NODE_HEIGHT }),
  );
  graph.edges
    .filter((edge) => keys.has(edge.fromKey) && keys.has(edge.toKey))
    .forEach((edge) => layout.setEdge(edge.fromKey, edge.toKey));
  dagre.layout(layout);
  return Object.fromEntries(
    graph.nodes.map((node) => {
      const point = layout.node(node.key);
      return [
        node.key,
        { x: point.x - NODE_WIDTH / 2, y: point.y - NODE_HEIGHT / 2 },
      ];
    }),
  );
};

interface GraphAuthorProps {
  graph: JobChainTemplateGraph;
  metadata: JobChainTemplateMetadata;
  editable: boolean;
  selection: string | null;
  onSelect: (key: string | null) => void;
  onChange: (graph: JobChainTemplateGraph) => void;
}

export const GraphAuthor = (props: GraphAuthorProps) => (
  <ReactFlowProvider>
    <GraphAuthorWorkspace {...props} />
  </ReactFlowProvider>
);

const GraphAuthorWorkspace = ({
  graph,
  metadata,
  editable,
  selection,
  onSelect,
  onChange,
}: GraphAuthorProps) => {
  const { t } = useTranslate();
  const [search, setSearch] = useState('');
  const [removeNode, setRemoveNode] = useState<string | null>(null);
  const [connectionSource, setConnectionSource] = useState<string | null>(null);
  const [notice, setNotice] = useState('');
  // Presentation state only. Never pass positions or viewport to onChange or persist them.
  const [positions, setPositions] = useState(() => layoutPositions(graph));
  const canvasRef = useRef<HTMLDivElement>(null);
  const flow = useReactFlow<CanvasNode>();
  const selectedNode = graph.nodes.find((node) => node.key === selection);
  const directionLabel = useCallback(
    (direction: string) => {
      const keys: Record<string, Parameters<typeof t>[0]> = {
        left: 'jobChainTemplateGraphDirectionLeft',
        right: 'jobChainTemplateGraphDirectionRight',
        up: 'jobChainTemplateGraphDirectionUp',
        down: 'jobChainTemplateGraphDirectionDown',
      };
      return keys[direction] ? t(keys[direction]) : direction;
    },
    [t],
  );

  useEffect(() => {
    setPositions((current) => {
      const keys = new Set(graph.nodes.map((node) => node.key));
      if (
        Object.keys(current).length === keys.size &&
        [...keys].every((key) => current[key])
      )
        return current;
      const fallback = layoutPositions(graph);
      return Object.fromEntries(
        [...keys].map((key) => [key, current[key] || fallback[key]]),
      );
    });
    if (
      connectionSource &&
      (!editable || !graph.nodes.some((node) => node.key === connectionSource))
    )
      setConnectionSource(null);
    if (
      selection &&
      !graph.nodes.some((node) => node.key === selection) &&
      !graph.edges.some((edge) => edge.key === selection)
    )
      onSelect(null);
  }, [graph, editable, selection, connectionSource, onSelect]);

  const label = useCallback(
    (key: string) => {
      const index = graph.nodes.findIndex((node) => node.key === key);
      const node = graph.nodes[index];
      return node
        ? t('jobChainTemplateGraphNodeLabel', {
            label:
              metadata.templateJobs.find((job) => job.id === node.jobTemplateId)
                ?.name ||
              t('jobChainTemplateGraphTemplateJob', {
                jobTemplateId: node.jobTemplateId,
              }),
            index: index + 1,
          })
        : t('jobChainTemplateGraphMissingNode');
    },
    [graph.nodes, metadata.templateJobs, t],
  );

  const canConnect = useCallback(
    (source: string | null, target: string | null, exceptEdge?: string) =>
      editable &&
      !!source &&
      !!target &&
      source !== target &&
      graph.nodes.some((node) => node.key === source) &&
      graph.nodes.some((node) => node.key === target) &&
      !graph.edges.some(
        (edge) =>
          edge.key !== exceptEdge &&
          edge.fromKey === source &&
          edge.toKey === target,
      ),
    [editable, graph.nodes, graph.edges],
  );

  const connect = useCallback(
    (source: string, target: string) => {
      if (!canConnect(source, target)) {
        setNotice(t('jobChainTemplateGraphInvalidConnection'));
        return;
      }
      const key = crypto.randomUUID();
      onChange({
        ...graph,
        edges: [
          ...graph.edges,
          {
            key,
            id: null,
            fromKey: source,
            toKey: target,
            groupId: null,
            conditions: [],
          },
        ],
      });
      onSelect(key);
      setConnectionSource(null);
      setNotice(t('jobChainTemplateGraphConnectionAdded'));
    },
    [canConnect, graph, onChange, onSelect, t],
  );

  const selectNode = useCallback(
    (key: string) => {
      if (connectionSource) connect(connectionSource, key);
      else onSelect(key);
    },
    [connectionSource, connect, onSelect],
  );

  const selectEdge = useCallback(
    (key: string) => {
      setConnectionSource(null);
      onSelect(key);
    },
    [onSelect],
  );

  const addNode = useCallback(
    (jobId: number, pointer?: XYPosition) => {
      const job = metadata.templateJobs.find((item) => item.id === jobId);
      if (!editable || !job || job.isSupported === false) return;
      const bounds = canvasRef.current?.getBoundingClientRect();
      if (!bounds) return;
      const point = flow.screenToFlowPosition(
        pointer || {
          x: bounds.left + bounds.width / 2,
          y: bounds.top + bounds.height / 2,
        },
      );
      const offset = pointer ? 0 : (graph.nodes.length % 5) * 24;
      const key = crypto.randomUUID();
      setPositions((current) => ({
        ...current,
        [key]: {
          x: point.x - NODE_WIDTH / 2 + offset,
          y: point.y - NODE_HEIGHT / 2 + offset,
        },
      }));
      onChange({
        ...graph,
        nodes: [
          ...graph.nodes,
          {
            key,
            id: null,
            jobTemplateId: jobId,
            joinPolicy: 'AnyLive',
            deleted: null,
          },
        ],
      });
      onSelect(key);
      setConnectionSource(null);
      setNotice(t('jobChainTemplateGraphNodeAdded'));
    },
    [editable, metadata.templateJobs, flow, graph, onChange, onSelect, t],
  );

  const requestRemoveNode = useCallback(
    (key: string) => {
      if (editable && graph.nodes.some((node) => node.key === key))
        setRemoveNode(key);
    },
    [editable, graph.nodes],
  );

  const removeEdge = useCallback(
    (key: string) => {
      if (!editable || !graph.edges.some((edge) => edge.key === key)) return;
      onChange({
        ...graph,
        edges: graph.edges.filter((edge) => edge.key !== key),
      });
      onSelect(null);
    },
    [editable, graph, onChange, onSelect],
  );

  const onBeforeDelete = useCallback<OnBeforeDelete<CanvasNode>>(
    async ({ nodes, edges }) => {
      if (!editable || removeNode) return false;
      if (nodes.length) requestRemoveNode(nodes[0].id);
      else if (edges.length) removeEdge(edges[0].id);
      // All removals go through the business-graph callbacks, including node confirmation.
      return false;
    },
    [editable, removeNode, requestRemoveNode, removeEdge],
  );

  const onNodesChange = useCallback<OnNodesChange<CanvasNode>>(
    (changes) => {
      setPositions((current) => {
        let next = current;
        for (const change of changes) {
          if (
            change.type !== 'position' ||
            !change.position ||
            !graph.nodes.some((node) => node.key === change.id)
          )
            continue;
          const previous = current[change.id];
          if (
            previous?.x === change.position.x &&
            previous?.y === change.position.y
          )
            continue;
          if (next === current) next = { ...current };
          next[change.id] = change.position;
        }
        return next;
      });
      for (const change of changes) {
        if (
          change.type === 'select' &&
          change.selected &&
          change.id !== selection
        ) {
          onSelect(change.id);
          break;
        }
      }
    },
    [graph.nodes, selection, onSelect],
  );

  const onEdgesChange = useCallback<OnEdgesChange>(
    (changes) => {
      for (const change of changes) {
        if (
          change.type === 'select' &&
          change.selected &&
          change.id !== selection
        ) {
          onSelect(change.id);
          break;
        }
      }
    },
    [selection, onSelect],
  );

  const onConnect = useCallback(
    (connection: Connection) => connect(connection.source, connection.target),
    [connect],
  );
  const isValidConnection = useCallback(
    (connection: Connection | Edge) =>
      canConnect(connection.source, connection.target),
    [canConnect],
  );
  const onReconnect = useCallback(
    (edge: Edge, connection: Connection) => {
      if (!canConnect(connection.source, connection.target, edge.id)) return;
      onChange({
        ...graph,
        edges: graph.edges.map((item) =>
          item.key === edge.id
            ? { ...item, fromKey: connection.source, toKey: connection.target }
            : item,
        ),
      });
      onSelect(edge.id);
      setConnectionSource(null);
    },
    [canConnect, graph, onChange, onSelect],
  );
  const onNodeClick = useCallback(
    (_event: React.MouseEvent, node: CanvasNode) => selectNode(node.id),
    [selectNode],
  );
  const onEdgeClick = useCallback(
    (_event: React.MouseEvent, edge: Edge) => selectEdge(edge.id),
    [selectEdge],
  );
  const onPaneClick = useCallback(() => {
    onSelect(null);
    setConnectionSource(null);
  }, [onSelect]);
  const onDrop = useCallback(
    (event: React.DragEvent) => {
      if (!editable || !event.dataTransfer.types.includes(JOB_DRAG_TYPE))
        return;
      event.preventDefault();
      addNode(Number(event.dataTransfer.getData(JOB_DRAG_TYPE)), {
        x: event.clientX,
        y: event.clientY,
      });
    },
    [editable, addNode],
  );
  const onDragOver = useCallback(
    (event: React.DragEvent) => {
      if (!editable || !event.dataTransfer.types.includes(JOB_DRAG_TYPE))
        return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
    },
    [editable],
  );

  const nodes = useMemo<CanvasNode[]>(
    () =>
      graph.nodes.map((node) => ({
        id: node.key,
        type: 'templateJob',
        position: positions[node.key] || { x: 0, y: 0 },
        selected: selection === node.key,
        ariaLabel: label(node.key),
        data: {
          label: label(node.key),
          joinPolicy: node.joinPolicy,
          deleted: node.deleted === true,
          start: !graph.edges.some((edge) => edge.toKey === node.key),
          end: !graph.edges.some((edge) => edge.fromKey === node.key),
          connectionSource: connectionSource === node.key,
        },
      })),
    [graph, positions, selection, connectionSource, label],
  );

  const edges = useMemo<Edge[]>(
    () =>
      graph.edges
        .filter(
          (edge) =>
            graph.nodes.some((node) => node.key === edge.fromKey) &&
            graph.nodes.some((node) => node.key === edge.toKey),
        )
        .map((edge) => ({
          id: edge.key,
          source: edge.fromKey,
          target: edge.toKey,
          type: 'smoothstep',
          selected: selection === edge.key,
          label:
            edge.conditions.length === 1
              ? edgeConditionSummary(edge.conditions, t)
              : edge.conditions.length
                ? t('jobChainTemplateGraphConditionCount', {
                    count: edge.conditions.length,
                  })
                : t('jobChainTemplateGraphAlways'),
          ariaLabel: t('jobChainTemplateGraphEdgeAriaLabel', {
            from: label(edge.fromKey),
            to: label(edge.toKey),
          }),
          markerEnd: { type: MarkerType.ArrowClosed, color: '#456d90' },
          style: {
            stroke: selection === edge.key ? '#1677ff' : '#456d90',
            strokeWidth: selection === edge.key ? 2.5 : 1.5,
          },
          labelStyle: { fill: '#29445d', fontSize: 11 },
          labelBgStyle: { fill: '#fff' },
        })),
    [graph, selection, label, t],
  );

  return (
    <>
      <GraphWorkspace>
        <header className="graph-toolbar">
          <div className="graph-toolbar-title">
            <h2>{t('jobChainTemplateGraphTitle')}</h2>
            <small>
              {editable
                ? t('jobChainTemplateGraphEditableInstructions')
                : t('jobChainTemplateGraphReadOnlyInstructions')}{' '}
              {t('jobChainTemplateGraphLayoutNotSaved')}
            </small>
          </div>
          <Button
            disabled={!graph.nodes.length}
            onClick={() => {
              setPositions(layoutPositions(graph));
              requestAnimationFrame(() => {
                void flow.fitView({ padding: 0.2, maxZoom: 1, duration: 200 });
              });
            }}
          >
            {t('jobChainTemplateGraphAutoLayout')}
          </Button>
          <Button
            disabled={!graph.nodes.length}
            onClick={() =>
              void flow.fitView({ padding: 0.2, maxZoom: 1, duration: 200 })
            }
          >
            {t('jobChainTemplateGraphFitView')}
          </Button>
          {editable && (
            <>
              <Button
                disabled={!selectedNode}
                onClick={() => {
                  if (selectedNode) {
                    setConnectionSource(selectedNode.key);
                    setNotice('');
                  }
                }}
              >
                {t('jobChainTemplateGraphConnectFromSelected')}
              </Button>
              <Button
                disabled={!selection}
                onClick={() => {
                  if (selectedNode) requestRemoveNode(selectedNode.key);
                  else if (selection) removeEdge(selection);
                }}
              >
                {t('jobChainTemplateGraphDeleteSelection')}
              </Button>
            </>
          )}
        </header>
        {connectionSource ? (
          <div className="graph-notice" role="status">
            {t('jobChainTemplateGraphSelectDestination', {
              label: label(connectionSource),
            })}
            <Button onClick={() => setConnectionSource(null)}>
              {t('jobChainTemplateGraphCancelConnection')}
            </Button>
          </div>
        ) : notice ? (
          <div className="graph-notice" role="status">
            {notice}
          </div>
        ) : null}
        <div className="graph-columns">
          <section
            className="graph-library"
            aria-label={t('jobChainTemplateGraphOutline')}
          >
            {editable && (
              <>
                <h3>{t('jobChainTemplateGraphTemplateJobs')}</h3>
                <label>
                  {t('jobChainTemplateGraphSearchTemplateJobs')}
                  <Input
                    type="search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                </label>
                <p>
                  <small>{t('jobChainTemplateGraphAddTaskInstructions')}</small>
                </p>
                <ul className="graph-job-list">
                  {metadata.templateJobs
                    .filter((job) =>
                      `${job.name} ${job.description || ''} ${job.jobType}`
                        .toLowerCase()
                        .includes(search.toLowerCase()),
                    )
                    .map((job) => (
                      <li key={job.id}>
                        <Button
                          className="graph-job"
                          aria-label={t('jobChainTemplateGraphAddJob', {
                            name: job.name,
                          })}
                          title={
                            job.isSupported === false
                              ? t('jobChainTemplateGraphUnsupportedJobType', {
                                  jobType: job.jobType,
                                })
                              : job.description || job.name
                          }
                          disabled={job.isSupported === false}
                          draggable={job.isSupported !== false}
                          onDragStart={(event) => {
                            if (!editable || job.isSupported === false) {
                              event.preventDefault();
                              return;
                            }
                            event.dataTransfer.setData(
                              JOB_DRAG_TYPE,
                              String(job.id),
                            );
                            event.dataTransfer.effectAllowed = 'copy';
                          }}
                          onClick={() => addNode(job.id)}
                        >
                          <span className="graph-job-grip" aria-hidden="true">
                            ⠿
                          </span>
                          <span>
                            <strong>{job.name}</strong>
                            <small>
                              {job.jobType}
                              {job.isSupported === false &&
                                ` · ${t('jobChainTemplateGraphUnsupported')}`}
                            </small>
                          </span>
                        </Button>
                      </li>
                    ))}
                </ul>
              </>
            )}
            <div className="graph-outline">
              <h3>{t('jobChainTemplateGraphOutline')}</h3>
              <h3>{t('jobChainTemplateGraphNodes')}</h3>
              <ol>
                {graph.nodes.map((node) => (
                  <li key={node.key}>
                    <Button
                      className="outline-select"
                      id={`node-${node.key}`}
                      aria-pressed={selection === node.key}
                      onClick={() => selectNode(node.key)}
                    >
                      {label(node.key)}
                      {selection === node.key
                        ? ` · ${t('jobChainTemplateGraphSelected')}`
                        : ''}
                    </Button>
                    {editable && (
                      <Button
                        className="outline-remove"
                        aria-label={t('jobChainTemplateGraphRemoveNode', {
                          label: label(node.key),
                        })}
                        onClick={() => requestRemoveNode(node.key)}
                      >
                        ×
                      </Button>
                    )}
                  </li>
                ))}
              </ol>
              <h3>{t('jobChainTemplateGraphEdges')}</h3>
              <ol>
                {graph.edges.map((edge) => (
                  <li key={edge.key}>
                    <Button
                      className="outline-select"
                      id={`edge-${edge.key}`}
                      aria-pressed={selection === edge.key}
                      onClick={() => selectEdge(edge.key)}
                    >
                      {label(edge.fromKey)} → {label(edge.toKey)}
                      {selection === edge.key
                        ? ` · ${t('jobChainTemplateGraphSelected')}`
                        : ''}
                    </Button>
                    {editable && (
                      <Button
                        className="outline-remove"
                        aria-label={t('jobChainTemplateGraphRemoveEdge', {
                          from: label(edge.fromKey),
                          to: label(edge.toKey),
                        })}
                        onClick={() => removeEdge(edge.key)}
                      >
                        ×
                      </Button>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          </section>
          <div
            className="graph-canvas"
            ref={canvasRef}
            role="region"
            aria-label={t('jobChainTemplateGraphWorkflowAriaLabel', {
              nodes: graph.nodes.length,
              edges: graph.edges.length,
            })}
          >
            <ReactFlow<CanvasNode>
              nodes={nodes}
              edges={edges}
              nodeTypes={nodeTypes}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onNodeClick={onNodeClick}
              onEdgeClick={onEdgeClick}
              onPaneClick={onPaneClick}
              onConnect={onConnect}
              isValidConnection={isValidConnection}
              onReconnect={onReconnect}
              edgesReconnectable={editable}
              onBeforeDelete={onBeforeDelete}
              onDrop={onDrop}
              onDragOver={onDragOver}
              nodesDraggable
              nodesConnectable={editable}
              deleteKeyCode={editable ? ['Backspace', 'Delete'] : null}
              multiSelectionKeyCode={null}
              selectionKeyCode={null}
              nodesFocusable
              edgesFocusable
              snapToGrid
              snapGrid={[16, 16]}
              fitView
              fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
              minZoom={0.15}
              maxZoom={2}
              proOptions={{ hideAttribution: true }}
              ariaLabelConfig={{
                'node.a11yDescription.default': t(
                  'jobChainTemplateGraphReactFlowNodeDescription',
                ),
                'node.a11yDescription.keyboardDisabled': t(
                  'jobChainTemplateGraphReactFlowNodeKeyboardDisabled',
                ),
                'node.a11yDescription.ariaLiveMessage': ({ direction, x, y }) =>
                  t('jobChainTemplateGraphReactFlowNodeMoved', {
                    direction: directionLabel(direction),
                    x,
                    y,
                  }),
                'edge.a11yDescription.default': t(
                  'jobChainTemplateGraphReactFlowEdgeDescription',
                ),
                'controls.ariaLabel': t(
                  'jobChainTemplateGraphReactFlowControls',
                ),
                'controls.zoomIn.ariaLabel': t(
                  'jobChainTemplateGraphReactFlowZoomIn',
                ),
                'controls.zoomOut.ariaLabel': t(
                  'jobChainTemplateGraphReactFlowZoomOut',
                ),
                'controls.fitView.ariaLabel': t(
                  'jobChainTemplateGraphReactFlowFitView',
                ),
                'controls.interactive.ariaLabel': t(
                  'jobChainTemplateGraphReactFlowToggleInteractivity',
                ),
                'minimap.ariaLabel': t('jobChainTemplateGraphReactFlowMinimap'),
                'handle.ariaLabel': t('jobChainTemplateGraphReactFlowHandle'),
              }}
            >
              <Background gap={16} color="#d5deea" />
              <MiniMap pannable zoomable style={{ width: 130, height: 85 }} />
              <Controls showInteractive={false} />
              {!graph.nodes.length && (
                <Panel position="top-center">
                  <div className="graph-empty">
                    {editable
                      ? t('jobChainTemplateGraphEmptyEditable')
                      : t('jobChainTemplateGraphEmptyReadOnly')}
                  </div>
                </Panel>
              )}
            </ReactFlow>
          </div>
          <GraphProperties
            graph={graph}
            metadata={metadata}
            editable={editable}
            selection={selection}
            label={label}
            onSelect={onSelect}
            onChange={onChange}
          />
        </div>
      </GraphWorkspace>
      {removeNode && (
        <ConfirmDialog
          title={t('jobChainTemplateGraphRemoveNodeTitle')}
          confirm={t('jobChainTemplateGraphRemoveNodeConfirm')}
          onCancel={() => setRemoveNode(null)}
          onConfirm={() => {
            if (
              editable &&
              graph.nodes.some((node) => node.key === removeNode)
            ) {
              onChange({
                ...graph,
                nodes: graph.nodes.filter((node) => node.key !== removeNode),
                edges: graph.edges.filter(
                  (edge) =>
                    edge.fromKey !== removeNode && edge.toKey !== removeNode,
                ),
              });
              onSelect(null);
            }
            setRemoveNode(null);
          }}
        >
          <p>
            {t('jobChainTemplateGraphRemoveNodeDescription', {
              label: label(removeNode),
              count: graph.edges.filter(
                (edge) =>
                  edge.fromKey === removeNode || edge.toKey === removeNode,
              ).length,
            })}
          </p>
        </ConfirmDialog>
      )}
    </>
  );
};
